/**
 * useDeviceStatus
 *
 * Hook for individual components to poll their bound device's status.
 * Works in both editor and viewer mode.
 */

import { useState, useEffect, useRef } from "react";
import { buildWorkflowOrganizationApiRoute, getValidatedCurrentWorkflowOrganization } from "@/utils/workflow-utils";
import { workflowFetch } from "@/services/api/workflowApi";
import type { DeviceBinding } from "@/components/shared/rendering/InteractionContext";

interface DeviceStatus {
  isOn: boolean;
  brightness?: number;
  lastSeen?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

const POLL_INTERVAL_MS = 5000;

// Cache device status across components to avoid duplicate API calls
const statusCache: Map<string, { status: DeviceStatus; fetchedAt: number }> = new Map();
const CACHE_TTL_MS = 4000; // Slightly less than poll interval

async function fetchDeviceStatus(deviceId: string, orgSlug?: string): Promise<DeviceStatus | null> {
  // Check cache first
  const cached = statusCache.get(deviceId);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.status;
  }

  // Use provided org slug, fall back to context
  const resolvedSlug = orgSlug || getValidatedCurrentWorkflowOrganization()?.slug;
  if (!resolvedSlug) return null;

  try {
    const response = await workflowFetch(
      buildWorkflowOrganizationApiRoute(resolvedSlug, 'devices'),
      {
        headers: { Accept: 'application/json' },
        organizationSlug: resolvedSlug,
      },
    );
    if (!response.ok) {
      return null;
    }

    const result: unknown = await response.json();
    const data = isRecord(result) && isRecord(result.data) ? result.data : null;
    const devices = Array.isArray(data?.devices)
      ? data.devices
      : isRecord(result) && Array.isArray(result.devices)
        ? result.devices
        : [];
    const device = devices.find(
      (candidate) =>
        isRecord(candidate) &&
        candidate.integration === "kasa" &&
        candidate.deviceId === deviceId,
    );
    if (!device) return null;

    const meta = isRecord(device.integrationMeta) ? device.integrationMeta : {};
    const status: DeviceStatus = {
      isOn: meta.relayState === "on",
      brightness: typeof meta.brightness === "number" ? meta.brightness : undefined,
      lastSeen: typeof device.lastSeen === "string" ? device.lastSeen : undefined,
    };

    statusCache.set(deviceId, { status, fetchedAt: Date.now() });
    return status;
  } catch {
    return null;
  }
}

export function useDeviceStatus(binding: DeviceBinding | undefined): DeviceStatus | null {
  const [status, setStatus] = useState<DeviceStatus | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!binding) {
      setStatus(null);
      return;
    }

    const poll = async () => {
      const result = await fetchDeviceStatus(binding.deviceId, binding.orgSlug);
      if (result) setStatus(result);
    };

    poll(); // Initial fetch
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [binding?.deviceId]);

  return status;
}
